const ID_QUERY = /^#?(\d+)$/;

const normalize = (text = '') => text.toLowerCase();

// Unicode lowercasing can expand characters, so retain their original text positions.
const originalPositions = (text) => {
  const positions = [];
  let offset = 0;
  for (const character of text) {
    const position = { start: offset, end: offset + character.length };
    positions.push(...Array.from({ length: normalize(character).length }, () => position));
    offset = position.end;
  }
  return positions;
};

export const searchTerms = (query) => normalize(query).split(/\s+/).filter(Boolean);

export const matchesSearch = (ticket, query) => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return true;

  const idQuery = trimmedQuery.match(ID_QUERY);
  if (idQuery && String(ticket.id) === idQuery[1]) return true;

  const searchableText = normalize(`${ticket.title}\n${ticket.description}`);
  return searchTerms(trimmedQuery).every((term) => searchableText.includes(term));
};

export const containsAllTerms = (text, terms) => {
  const normalizedText = normalize(text);
  return terms.every((term) => normalizedText.includes(term));
};

export const highlightParts = (text, terms) => {
  if (!text || terms.length === 0) return [{ text: text ?? '', match: false }];

  const normalizedText = normalize(text);
  const positions = originalPositions(text);
  const marked = Array.from({ length: text.length }, () => false);
  for (const term of terms) {
    for (
      let index = normalizedText.indexOf(term);
      index !== -1;
      index = normalizedText.indexOf(term, index + 1)
    ) {
      marked.fill(true, positions[index].start, positions[index + term.length - 1].end);
    }
  }

  return marked.reduce((parts, match, index) => {
    const lastPart = parts.at(-1);
    if (lastPart?.match === match) {
      lastPart.text += text[index];
    } else {
      parts.push({ text: text[index], match });
    }
    return parts;
  }, []);
};

export const searchSnippet = (text, terms, radius = 60) => {
  const flatText = (text ?? '').replaceAll(/\s+/g, ' ').trim();
  const normalizedText = normalize(flatText);
  const positions = terms
    .map((term) => normalizedText.indexOf(term))
    .filter((position) => position !== -1);
  if (positions.length === 0) return null;

  const characters = Array.from(flatText);
  const firstMatchOffset = originalPositions(flatText)[Math.min(...positions)].start;
  const firstMatch = Array.from(flatText.slice(0, firstMatchOffset)).length;
  const start = Math.max(0, firstMatch - radius);
  const end = Math.min(characters.length, firstMatch + radius * 2);

  const excerpt = characters.slice(start, end).join('');
  return `${start > 0 ? '…' : ''}${excerpt}${end < characters.length ? '…' : ''}`;
};
